import { RefObject, useEffect } from 'react';
import { clientPortalApi } from './api';

// Mismo prefijo de canal `analysis:` que useProgrammingBridge — namespace
// propio (`coverage`, no `programming`) porque es una capacidad
// independiente (solo lectura, nunca dispara cálculos ni persiste nada).
const MSG_REQUEST = 'analysis:coverage:request';
const MSG_RESULT = 'analysis:coverage:result';

interface CoverageMessage {
  type: string;
  channelId: string;
  walkTimeMinutes?: number;
  walkingSpeedKmh?: number;
}

interface UseCoverageBridgeArgs {
  slug: string;
  analysisId: string;
  token: string | null;
  iframeRef: RefObject<HTMLIFrameElement>;
  channelIdRef: RefObject<string>;
  targetOriginRef: RefObject<string | null>;
}

/**
 * Puente `postMessage` para Cobertura poblacional peatonal — mismo patrón
 * que `useProgrammingBridge`: el template nunca hace fetch autenticado ni
 * conoce el JWT, React posee la sesión.
 */
export function useCoverageBridge({
  slug,
  analysisId,
  token,
  iframeRef,
  channelIdRef,
  targetOriginRef,
}: UseCoverageBridgeArgs): void {
  useEffect(() => {
    if (!token) return undefined;
    const authToken = token;

    function postToIframe(message: Record<string, unknown>): void {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(message, targetOriginRef.current ?? '*');
    }

    function isTrustedSource(event: MessageEvent): boolean {
      if (event.source !== iframeRef.current?.contentWindow) return false;
      if (targetOriginRef.current && event.origin !== targetOriginRef.current) return false;
      return true;
    }

    function handleRequest(msg: CoverageMessage): void {
      if (typeof msg.walkTimeMinutes !== 'number' || typeof msg.walkingSpeedKmh !== 'number') {
        return;
      }
      clientPortalApi
        .getPedestrianCoverage(slug, analysisId, authToken, {
          walkTimeMinutes: msg.walkTimeMinutes,
          walkingSpeedKmh: msg.walkingSpeedKmh,
        })
        .then(result => {
          postToIframe({ type: MSG_RESULT, channelId: msg.channelId, ok: true, result });
        })
        .catch(error => {
          const message =
            (error?.response?.data?.message as string | undefined) ??
            'No se pudo calcular la cobertura poblacional.';
          postToIframe({ type: MSG_RESULT, channelId: msg.channelId, ok: false, message });
        });
    }

    function onMessage(event: MessageEvent<CoverageMessage>): void {
      if (!isTrustedSource(event)) return;
      const msg = event.data;
      if (!msg || msg.channelId !== channelIdRef.current) return;
      if (msg.type === MSG_REQUEST) handleRequest(msg);
    }

    window.addEventListener('message', onMessage);
    return (): void => {
      window.removeEventListener('message', onMessage);
    };
  }, [slug, analysisId, token, iframeRef, channelIdRef, targetOriginRef]);
}
